/**
 * Impostazioni, sezione «Tasse»: il regime fiscale, che cambia il conto di
 * ogni mese.
 */

import { useState } from "react";
import { Check, Landmark } from "lucide-react";
import {
  ETICHETTE_REGIME,
  REGIMI_FISCALI,
  type RegimeFiscale,
} from "@otto/shared";
import { api } from "../../lib/api.ts";
import { messaggioDi } from "../../lib/errori.ts";
import { useStore } from "../../lib/store.tsx";
import { cx } from "../../ui/primitives.tsx";
import { Scheda } from "./comuni.tsx";

export function SezioneTasse() {
  const store = useStore();
  const account = store.account;
  const [busy, setBusy] = useState(false);

  /**
   * Il regime fiscale non passa dalla chat: il modello non deve poter cambiare
   * la base su cui si calcolano i costi dell'host.
   */
  const scegliRegime = async (regime: RegimeFiscale) => {
    setBusy(true);
    try {
      const nuovo = account?.regimeFiscale === regime ? null : regime;
      await api.setFisco(nuovo, account?.annoAvvioAttivita ?? null);
      await store.refreshAccount();
      store.notify(
        nuovo === null
          ? "Regime tolto: nelle stime non do per scontato niente."
          : `Regime impostato. Rifaccio i conti da qui in avanti.`,
      );
    } catch (errore) {
      store.notify(messaggioDi(errore));
    } finally {
      setBusy(false);
    }
  };

  const scegliAnno = async (valore: string) => {
    const anno = valore.trim() === "" ? null : Number(valore);
    if (anno !== null && !Number.isInteger(anno)) return;
    if (anno === (account?.annoAvvioAttivita ?? null)) return;
    setBusy(true);
    try {
      await api.setFisco(account?.regimeFiscale ?? null, anno);
      await store.refreshAccount();
    } catch (errore) {
      store.notify(messaggioDi(errore));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Scheda
      Icona={Landmark}
      titolo="Come sei messo con le tasse"
      testo="È l'unica cosa del conto che devo chiederti. Cambia parecchio: l'IVA sulla commissione del canale la recuperi solo in regime ordinario, e sul 15,5% di Airbnb fa la differenza fra 15,5% e 18,91%."
    >
      <div className="grid gap-2 sm:grid-cols-3">
        {REGIMI_FISCALI.map((regime) => {
          const scelto = account?.regimeFiscale === regime;
          return (
            <button
              key={regime}
              type="button"
              disabled={busy}
              onClick={() => scegliRegime(regime)}
              className={cx(
                "flex items-start gap-2.5 rounded-card border px-3.5 py-3 text-left text-[13.5px] transition-all duration-quick",
                "disabled:pointer-events-none disabled:opacity-45",
                scelto
                  ? "border-ink bg-surface-muted font-semibold text-ink"
                  : "border-line text-ink-soft hover:border-line-strong hover:text-ink",
              )}
            >
              <span
                className={cx(
                  "mt-0.5 grid size-4 shrink-0 place-items-center rounded-full border",
                  scelto
                    ? "border-ink bg-ink text-ink-inverse"
                    : "border-line-strong",
                )}
              >
                {scelto ? <Check size={10} strokeWidth={3.5} /> : null}
              </span>
              {ETICHETTE_REGIME[regime]}
            </button>
          );
        })}
      </div>

      {account?.regimeFiscale === "forfettario" ? (
        <div className="animate-rise mt-4 flex flex-wrap items-center gap-2.5 rounded-card bg-surface-muted px-3.5 py-3">
          <label
            htmlFor="anno-avvio"
            className="text-[13px] font-semibold text-ink"
          >
            Attività avviata nel
          </label>
          <input
            id="anno-avvio"
            type="number"
            min={1990}
            max={2100}
            defaultValue={account.annoAvvioAttivita ?? ""}
            placeholder="anno"
            disabled={busy}
            onBlur={(e) => scegliAnno(e.currentTarget.value)}
            className="w-24 rounded-field border border-line bg-surface px-2 py-1 text-[13.5px] tabular-nums text-ink"
          />
          <span className="text-[12px] text-ink-faint">
            decide se la sostitutiva è ancora al 5% o già al 15%
          </span>
        </div>
      ) : null}
    </Scheda>
  );
}
